/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { AcmsAircraftResponse } from '../models/acms-aircraft-response';
import { CreateAcmsAircraftRequest } from '../models/create-acms-aircraft-request';
import { GenericResponse } from '../models/generic-response';
import { GetAcmsAircraftResponse } from '../models/get-acms-aircraft-response';
import { UpdateAcmsAircraftRequest } from '../models/update-acms-aircraft-request';

/**
 * ACMS Aircraft APIs
 */
@Injectable({ providedIn: 'root' })
export class AcmsAircraftService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllAcmsAircraft()` */
  static readonly GetAllAcmsAircraftPath = '/api/v1/acms-aircraft';

  /**
   * Get list of ACMS aircrafts.
   *
   * Gets the list of all the ACMS aircraft with any of the filters applied.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllAcmsAircraft()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAcmsAircraft$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - tailName
       *   - aircraftType
       */
      search?: string;

      /**
       * This enables filtering of aircraft based on the client id.
       */
      clientId?: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetAcmsAircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftService.GetAllAcmsAircraftPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('clientId', params.clientId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetAcmsAircraftResponse>;
        })
      );
  }

  /**
   * Get list of ACMS aircrafts.
   *
   * Gets the list of all the ACMS aircraft with any of the filters applied.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllAcmsAircraft$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllAcmsAircraft(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to following fields:
       *   - tailName
       *   - aircraftType
       */
      search?: string;

      /**
       * This enables filtering of aircraft based on the client id.
       */
      clientId?: number;
    },
    context?: HttpContext
  ): Observable<GetAcmsAircraftResponse> {
    return this.getAllAcmsAircraft$Response(params, context).pipe(
      map(
        (
          r: StrictHttpResponse<GetAcmsAircraftResponse>
        ): GetAcmsAircraftResponse => r.body
      )
    );
  }

  /** Path part for operation `createAcmsAircraft()` */
  static readonly CreateAcmsAircraftPath = '/api/v1/acms-aircraft';

  /**
   * Create new ACMS aircraft.
   *
   * This API creates a new ACMS aircraft with provided fields.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createAcmsAircraft()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAcmsAircraft$Response(
    params: {
      body: CreateAcmsAircraftRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AcmsAircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftService.CreateAcmsAircraftPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AcmsAircraftResponse>;
        })
      );
  }

  /**
   * Create new ACMS aircraft.
   *
   * This API creates a new ACMS aircraft with provided fields.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createAcmsAircraft$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createAcmsAircraft(
    params: {
      body: CreateAcmsAircraftRequest;
    },
    context?: HttpContext
  ): Observable<AcmsAircraftResponse> {
    return this.createAcmsAircraft$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<AcmsAircraftResponse>): AcmsAircraftResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `getAcmsAircraftById()` */
  static readonly GetAcmsAircraftByIdPath = '/api/v1/acms-aircraft/{id}';

  /**
   * fetch aircraft details.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAcmsAircraftById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAcmsAircraftById$Response(
    params: {
      /**
       * The aircraft id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AcmsAircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftService.GetAcmsAircraftByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AcmsAircraftResponse>;
        })
      );
  }

  /**
   * fetch aircraft details.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAcmsAircraftById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAcmsAircraftById(
    params: {
      /**
       * The aircraft id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<AcmsAircraftResponse> {
    return this.getAcmsAircraftById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<AcmsAircraftResponse>): AcmsAircraftResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `deleteAcmsAircraftById()` */
  static readonly DeleteAcmsAircraftByIdPath = '/api/v1/acms-aircraft/{id}';

  /**
   * Delete ACMS aircraft  by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteAcmsAircraftById()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAcmsAircraftById$Response(
    params: {
      /**
       * The aircraft id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftService.DeleteAcmsAircraftByIdPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Delete ACMS aircraft  by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteAcmsAircraftById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteAcmsAircraftById(
    params: {
      /**
       * The aircraft id that needs to be delete
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.deleteAcmsAircraftById$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateAcmsAircraftById()` */
  static readonly UpdateAcmsAircraftByIdPath = '/api/v1/acms-aircraft/{id}';

  /**
   * Update ACMS Aircraft.
   *
   * This API update ACMS aircraft.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateAcmsAircraftById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAcmsAircraftById$Response(
    params: {
      /**
       * The aircraft id that needs to be updated
       */
      id: number;
      body: UpdateAcmsAircraftRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<AcmsAircraftResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      AcmsAircraftService.UpdateAcmsAircraftByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AcmsAircraftResponse>;
        })
      );
  }

  /**
   * Update ACMS Aircraft.
   *
   * This API update ACMS aircraft.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateAcmsAircraftById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateAcmsAircraftById(
    params: {
      /**
       * The aircraft id that needs to be updated
       */
      id: number;
      body: UpdateAcmsAircraftRequest;
    },
    context?: HttpContext
  ): Observable<AcmsAircraftResponse> {
    return this.updateAcmsAircraftById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<AcmsAircraftResponse>): AcmsAircraftResponse =>
          r.body
      )
    );
  }
}
